import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X, Compass, Target, Brain, Eye, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const TOUR_STEPS = [
  {
    target: '[data-testid="sun-map-card"]',
    icon: Compass,
    en: { title: 'Your Sun Map', body: 'This radar chart maps 12 areas of your life. Gold is where you are now. The bigger the shape, the more aligned you are.' },
    tr: { title: 'Güneş Haritanız', body: 'Bu radar grafik hayatınızın 12 alanını haritalandırır. Altın rengi mevcut durumunuzu gösterir.' },
    position: 'bottom',
  },
  {
    target: '[data-testid="navigator-today-panel"]',
    icon: Target,
    en: { title: "Today's Direction", body: "Your daily navigation hub. After your morning check-in, you'll see your state, your one win for the day, and your next action — right here." },
    tr: { title: 'Bugünün Yönü', body: 'Günlük navigasyon merkeziniz. Sabah kalibrasyonunuzdan sonra durumunuzu, günün hedefini ve sonraki adımınızı burada göreceksiniz.' },
    position: 'bottom',
  },
  {
    target: '[data-testid="missions-section"]',
    icon: Target,
    en: { title: 'Active Missions', body: 'Your focused actions for the week. Add missions from the Navigator, or create your own. Daily, weekly, or monthly — you choose the rhythm.' },
    tr: { title: 'Aktif Misyonlar', body: 'Haftanın odaklanmış eylemleri. Navigatörden misyon ekleyin veya kendiniz oluşturun.' },
    position: 'top',
  },
  {
    target: '[data-testid="shadow-panel-toggle"]',
    icon: Eye,
    en: { title: 'Shadow Detection', body: "The Oracle sees what you avoid. This reveals your resistance patterns — the hidden blocks keeping you stuck. It's not comfortable, but it's where growth lives." },
    tr: { title: 'Gölge Algılama', body: 'Kahin, kaçındığınız şeyleri görür. Direniş kalıplarınızı ortaya çıkarır — sizi tutan gizli engeller.' },
    position: 'top',
  },
  {
    target: '[data-testid="quick-action-coaching"]',
    icon: MessageSquare,
    en: { title: 'Your AI Coach', body: "When you need to think something through — a decision, a block, a direction — your coach is here. It knows your map, your patterns, and your priorities." },
    tr: { title: 'AI Koçunuz', body: 'Bir karar, bir engel, bir yön hakkında düşünmeniz gerektiğinde koçunuz burada. Haritanızı, kalıplarınızı ve önceliklerinizi bilir.' },
    position: 'top',
  },
];

export const DashboardTour = ({ onComplete }) => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(true);

  const positionTooltip = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Small delay to let scroll finish
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
      }, 400);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [positionTooltip]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem('in_show_tour', '');
    localStorage.setItem('in_tour_completed', 'true');
    if (onComplete) onComplete();
  };

  if (!visible) return null;

  const step = TOUR_STEPS[currentStep];
  const content = step[language] || step.en;
  const Icon = step.icon;
  const isLast = currentStep === TOUR_STEPS.length - 1;

  // Calculate tooltip position
  let tooltipStyle = {};
  if (targetRect) {
    const isTop = step.position === 'top' || targetRect.top > window.innerHeight / 2;
    if (isTop) {
      tooltipStyle = {
        position: 'fixed',
        bottom: (window.innerHeight - targetRect.top + 16) + 'px',
        left: '16px',
        right: '16px',
      };
    } else {
      tooltipStyle = {
        position: 'fixed',
        top: Math.min(window.innerHeight - 220, targetRect.bottom + 16) + 'px',
        left: '16px',
        right: '16px',
      };
    }
  } else {
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '16px',
      right: '16px',
      transform: 'translateY(-50%)',
    };
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-[9998]" onClick={handleClose} />

      {/* Highlight ring around target */}
      {targetRect && (
        <div
          className="fixed z-[9999] rounded-2xl ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-black/80 pointer-events-none"
          style={{
            top: targetRect.top - 2 + 'px',
            left: targetRect.left - 2 + 'px',
            width: targetRect.width + 4 + 'px',
            height: targetRect.height + 4 + 'px',
          }}
        />
      )}

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={tooltipStyle}
          className="z-[10000] w-full max-w-[340px]"
          data-testid="tour-tooltip"
        >
          <div className="bg-[#1A1A1B] border border-[#D4AF37]/30 rounded-2xl p-5 shadow-[0_0_30px_rgba(212,175,55,0.15)] mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-[#F8F5F2] text-base">{content.title}</h3>
              </div>
              <button onClick={handleClose} className="text-[#6B7280] hover:text-white transition-colors" data-testid="tour-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <p className="text-[#A1A1AA] font-sans text-sm leading-relaxed mb-4">
              {content.body}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between">
              {/* Progress dots */}
              <div className="flex gap-1.5">
                {TOUR_STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === currentStep ? 'w-6 bg-[#D4AF37]' : i < currentStep ? 'w-3 bg-[#D4AF37]/50' : 'w-3 bg-white/10'
                    }`}
                  />
                ))}
              </div>

              {/* Next / Finish */}
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D4AF37] text-[#030305] font-sans text-xs uppercase tracking-wider hover:bg-[#FFBF00] transition-all"
                data-testid="tour-next"
              >
                {isLast ? (language === 'tr' ? 'Başla' : "Let's Go") : (language === 'tr' ? 'Sonraki' : 'Next')}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default DashboardTour;
